import { Component, type ErrorInfo, type ReactNode } from 'react';
import { L } from '@/i18n';

/** Recent errors, kept in memory so Settings can show them for troubleshooting. */
export const errorLog: { at: number; where: string; message: string }[] = [];

export function recordError(where: string, err: unknown) {
  const message = err instanceof Error ? err.message : String(err);
  errorLog.push({ at: Date.now(), where, message });
  if (errorLog.length > 30) errorLog.shift();
  console.error(`[Studio] ${where}:`, err);
}

/**
 * Contains a failure to the part of the screen that broke. The rest of Studio
 * (navigation, your data) keeps working and the learner gets a way forward.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; where: string; compact?: boolean; onReset?: () => void }, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    recordError(this.props.where, `${error.message}\n${info.componentStack ?? ''}`);
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render() {
    if (!this.state.error) return this.props.children;
    if (this.props.compact) {
      return (
        <div className="callout amber" role="alert">
          <span>
            {L('This part could not be shown.', 'Denne delen kunne ikke vises.')}{' '}
            <button className="link-btn" onClick={this.reset}>{L('Try again', 'Prøv igjen')}</button>
          </span>
        </div>
      );
    }
    return (
      <div className="page" role="alert">
        <div className="plate stack" style={{ maxWidth: 640 }}>
          <h2>{L('Something went wrong on this page', 'Noe gikk galt på denne siden')}</h2>
          <p className="muted">
            {L('Your progress is safe — it is saved on this device. You can try again or go back to Today.', 'Fremgangen din er trygg — den er lagret på denne enheten. Du kan prøve igjen eller gå tilbake til I dag.')}
          </p>
          <code className="tiny faint" style={{ whiteSpace: 'pre-wrap' }}>{this.state.error.message}</code>
          <div className="row">
            <button className="btn primary" onClick={this.reset}>{L('Try again', 'Prøv igjen')}</button>
            <a className="btn ghost" href="#/today" onClick={this.reset}>{L('Go to Today', 'Gå til I dag')}</a>
          </div>
        </div>
      </div>
    );
  }
}
